/**
 * InsertColumnMap — renders the INSERT target table + the optional column list
 * as a row of pills. When `editContext` is supplied (plain-INSERT usage only —
 * MERGE's `WhenBranchCard` mount stays scaffolded, decision 13) the card's
 * "Change table" swaps the target (`change-dml-target-table`), the footer
 * `[+ add column]` opens the column picker (`DmlColumnPicker` — schema columns,
 * or a typed name), each pill's name swaps that column (`modify-insert-column`)
 * and each pill's `×` commits a `remove-insert-column` op via SR-Edit.
 *
 * Column ops are VALUES-form only (decision 4, see `checkColumnOpsShape` in
 * `insertColumnMapHelpers.ts`): both add and remove require an explicit,
 * non-empty column list AND `bodyKind === 'values'`, else they stay disabled
 * with a reason-specific tooltip (implicit list / INSERT…SELECT). Adding no
 * longer needs the schema snapshot — without one the picker takes a typed
 * name — but stays disabled once every known column is listed. Remove gates
 * on `editReady` (editContext + snapshot, decision 7) plus a last-column guard
 * (decision 5).
 *
 * Test-id prefix is parameterized so MERGE's `INSERT` action body can reuse the
 * component with its own namespaced ids; default keeps the original
 * `vqe-insert-*` family for plain INSERT renderers.
 */

import { AddButton } from '../common/AddButton';
import { DmlChangeTablePicker } from '../common/DmlChangeTablePicker';
import { ClauseCard } from '../select/ClauseCard';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { addInsertColumn } from '../../edit/edits/addInsertColumn';
import { vqeCopy } from '../../ux/vqeCopy';
import { isSameColumn } from '../../../../sql-resolver/scope/columnRefs';
import { resolveWrittenTableColumns } from '../update/assignmentColumns';
import { DmlColumnPicker } from '../update/DmlColumnPicker';
import type { InsertBodyKind } from './extractInsertOutline';
import { checkColumnOpsShape, recordEditFailure, safeExtractStructure } from './insertColumnMapHelpers';
import { InsertColumnPill } from './InsertColumnPill';

export interface InsertColumnMapProps {
  readonly target: string;
  readonly columns: readonly string[] | null;
  readonly title?: string;
  readonly testIdPrefix?: string;
  /** Present only for plain-INSERT mounts (decision 13 — MERGE stays scaffolded). */
  readonly editContext?: VqeEditContext;
  /** The outline's parsed body kind — drives the VALUES-form-only shape guard. */
  readonly bodyKind?: InsertBodyKind;
  /**
   * The statement's parsed VALUES row count (DML-37) — threaded in from
   * `InsertRenderer`'s `outline.valuesRows.length` so the enabled column-×
   * consequence tooltip can name the concrete row count. When omitted, the
   * tooltip falls back to count-free wording.
   */
  readonly valuesRowCount?: number;
}

export function InsertColumnMap({
  target,
  columns,
  title = 'Insert into',
  testIdPrefix = 'vqe-insert',
  editContext,
  bodyKind,
  valuesRowCount,
}: InsertColumnMapProps) {
  const shape = checkColumnOpsShape(columns, bodyKind);
  const editReady = !!editContext && !!editContext.service.getSchemaSnapshot();
  const table = editContext ? resolveWrittenTableColumns(editContext, 'insert') : null;
  // Exhaustion (every known column already listed) is not "editing
  // unavailable" — the disabled add button says the real reason.
  const allListed =
    !!table?.columns &&
    table.columns.every((c) => columns?.some((ref) => isSameColumn(ref, c.qualified.name, table.engine)));

  const addColumnFooter =
    shape.ok && editContext && table && columns && !allListed ? (
      <DmlColumnPicker
        testId={`${testIdPrefix}-add-column`}
        table={table}
        takenRefs={columns}
        qualifier={null}
        usage="insert"
        triggerLabel={vqeCopy.actions.addColumn}
        onPick={({ text }) => {
          addInsertColumn(text, toCommitCtx(editContext), {
            structure: safeExtractStructure(editContext),
          }).catch(recordEditFailure);
        }}
      />
    ) : (
      <AddButton
        label={vqeCopy.actions.addColumn}
        testId={`${testIdPrefix}-add-column`}
        tooltip={!shape.ok ? shape.reason : allListed ? vqeCopy.disabled.insertColumnsAllListed : undefined}
      />
    );

  return (
    <ClauseCard
      title={title}
      testId={`${testIdPrefix}-column-map`}
      footer={addColumnFooter}
    >
      {/* The target on its own line with Change table beside it — the same
          target row UPDATE / DELETE have — and the column list below. */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          className="rounded-md border border-border bg-muted/30 px-2 py-1 font-mono text-xs"
          data-testid={`${testIdPrefix}-target`}
        >
          {target}
        </span>
        {editContext ? <DmlChangeTablePicker target="insert" editContext={editContext} /> : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {columns === null ? (
          <span
            className="text-xs italic text-muted-foreground"
            data-testid={`${testIdPrefix}-columns-implicit`}
          >
            (all columns)
          </span>
        ) : columns.length === 0 ? (
          <span
            className="text-xs italic text-muted-foreground"
            data-testid={`${testIdPrefix}-columns-empty`}
          >
            (empty column list)
          </span>
        ) : (
          <ul
            className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0"
            data-testid={`${testIdPrefix}-columns`}
          >
            {columns.map((col, idx) => {
              const canRemove = shape.ok && !!editContext && editReady && columns.length > 1;
              return (
                <InsertColumnPill
                  key={`${col}-${idx}`}
                  testIdPrefix={testIdPrefix}
                  index={idx}
                  column={col}
                  columns={columns}
                  edit={editContext && table ? { editContext, table } : undefined}
                  canRemove={canRemove}
                  removeTooltip={
                    !shape.ok
                      ? shape.reason
                      : editContext && editReady && columns.length === 1
                        ? vqeCopy.disabled.insertColumnsLastOne
                        : undefined
                  }
                  removeHint={
                    canRemove
                      ? typeof valuesRowCount === 'number'
                        ? vqeCopy.hint.columnRemoveConsequence(valuesRowCount)
                        : vqeCopy.hint.columnRemoveConsequenceGeneric
                      : undefined
                  }
                />
              );
            })}
          </ul>
        )}
      </div>
    </ClauseCard>
  );
}
